/**
 * Country display for a two-letter ISO code.
 *
 * Both the flag and the name are derived rather than looked up: the flag is
 * the code's letters as regional indicators, and the name comes from `Intl`.
 * A hand-kept table would cover the two countries we serve today and render
 * a bare "GB" the day we serve a third.
 */

const REGIONAL_INDICATOR_A = 0x1f1e6;
const LETTER_A = "A".charCodeAt(0);

function isCountryCode(code: string): boolean {
  return /^[A-Za-z]{2}$/.test(code);
}

/** The flag emoji for a country code, or "" when the code is not one. */
export function countryFlag(code: string | null | undefined): string {
  if (!code || !isCountryCode(code)) return "";

  return [...code.toUpperCase()]
    .map((letter) =>
      String.fromCodePoint(
        REGIONAL_INDICATOR_A + letter.charCodeAt(0) - LETTER_A,
      ),
    )
    .join("");
}

/**
 * The country's name in the reader's locale — "Canada", not "CA".
 *
 * Falls back to the code itself when `Intl` does not recognise it, which
 * keeps an unexpected value visible instead of blanking the field.
 */
export function countryName(code: string | null | undefined): string {
  if (!code) return "";
  if (!isCountryCode(code)) return code;

  try {
    const display = new Intl.DisplayNames(undefined, { type: "region" });
    return display.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

/** Flag and name together: "🇨🇦 Canada". */
export function formatCountry(code: string | null | undefined): string {
  if (!code) return "";
  const flag = countryFlag(code);
  const name = countryName(code);
  return flag ? `${flag} ${name}` : name;
}
